import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Loader2, ShieldCheck, FileText, Mail, AlertCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EnviarEevidDialog (Paso 4 · v82)
 * --------------------------------------------------------------------
 * Envía la reclamación al banco por email certificado Eevid.
 *  - Remitente fijo: reclamaciones@lexduo.cat (cuenta por defecto del backend)
 *  - Destinatario: email del banco + sufijo ".eevid.com"
 *  - Adjuntos: documentos seleccionados (checkbox) + 2 primeras págs escritura
 */
export default function EnviarEevidDialog({ isOpen, onClose, reclamacionId, reclamacion, onSuccess }) {
  const [bankEmail, setBankEmail] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [incluirEscritura, setIncluirEscritura] = useState(true);
  const [seleccionados, setSeleccionados] = useState({});
  const [sending, setSending] = useState(false);

  const documentos = useMemo(() => reclamacion?.documentos || [], [reclamacion]);

  useEffect(() => {
    if (isOpen) {
      const ref = reclamacion?.numero_referencia || '';
      const banco = reclamacion?.entidad_bancaria_actual || 'la entidad';
      const cliente = reclamacion?.cliente_nombre || 'nuestro cliente';
      setBankEmail('');
      setAsunto(`Reclamación extrajudicial ${ref} - ${cliente}`.trim());
      setCuerpo(
        `Estimados señores de ${banco}:\n\n` +
        `Adjunto a la presente remitimos reclamación extrajudicial en nombre de ${cliente}, ` +
        `junto con la documentación acreditativa correspondiente.\n\n` +
        `Quedamos a la espera de su respuesta en el plazo legalmente establecido.\n\n` +
        `Atentamente,\nLEXDUO Abogados`
      );
      setIncluirEscritura(true);
      // Preseleccionar documentos de reclamación firmada
      const pre = {};
      (reclamacion?.documentos || []).forEach((d) => {
        if (['hoja_encargo_firmada', 'reclamacion', 'reclamacion_enviada'].includes(d.tipo)) {
          pre[d.id] = true;
        }
      });
      setSeleccionados(pre);
    }
  }, [isOpen, reclamacion]);

  const recipientPreview = useMemo(() => {
    const v = (bankEmail || '').trim();
    if (!v) return '';
    if (v.toLowerCase().endsWith('.eevid.com')) return v;
    return `${v}.eevid.com`;
  }, [bankEmail]);

  const toggleDoc = (id) => setSeleccionados((p) => ({ ...p, [id]: !p[id] }));

  const numSeleccionados = Object.values(seleccionados).filter(Boolean).length;

  const handleSend = async () => {
    if (!bankEmail.trim() || !bankEmail.includes('@')) {
      toast({ title: 'Email del banco requerido', description: 'Introduce un email válido (ej: reclamaciones@banco.com)', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('lexduo_token');
      const documento_ids = Object.entries(seleccionados).filter(([, v]) => v).map(([k]) => parseInt(k, 10));
      const res = await axios.post(
        `${API}/api/extrajudicial/reclamaciones/${reclamacionId}/enviar-eevid`,
        { bank_email: bankEmail.trim(), asunto, cuerpo, documento_ids, incluir_escritura: incluirEscritura },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: '✅ Enviado por Eevid',
        description: `Enviado a ${res.data.recipient} desde ${res.data.from} (${res.data.adjuntos?.length || 0} adjunto(s)). Respuesta del servidor: ${res.data.smtp_response || 'OK'}. Revisa la bandeja de ${res.data.from} para el acuse de Eevid.`,
      });
      onClose();
      onSuccess && onSuccess();
    } catch (error) {
      toast({
        title: 'Error en el envío',
        description: error.response?.data?.detail || 'No se pudo enviar el email certificado',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="eevid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Enviar al Banco por Eevid (certificado)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 flex items-start gap-2">
            <Mail className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Se enviará desde <strong>reclamaciones@lexduo.cat</strong> con certificación Eevid. El acuse llegará a esa misma cuenta.</span>
          </div>

          <div>
            <Label>Email del banco</Label>
            <Input
              data-testid="eevid-bank-email"
              placeholder="reclamaciones@banco.com"
              value={bankEmail}
              onChange={(e) => setBankEmail(e.target.value)}
            />
            {recipientPreview && (
              <p className="text-xs text-emerald-700 mt-1" data-testid="eevid-recipient-preview">
                Destinatario certificado: <strong>{recipientPreview}</strong>
              </p>
            )}
          </div>

          <div>
            <Label>Asunto</Label>
            <Input data-testid="eevid-asunto" value={asunto} onChange={(e) => setAsunto(e.target.value)} />
          </div>

          <div>
            <Label>Cuerpo del email</Label>
            <Textarea data-testid="eevid-cuerpo" rows={6} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} />
          </div>

          <div className="flex items-center space-x-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <Checkbox id="eevid-escritura" checked={incluirEscritura} onCheckedChange={setIncluirEscritura} data-testid="eevid-incluir-escritura" />
            <label htmlFor="eevid-escritura" className="text-sm font-medium text-indigo-900 cursor-pointer">
              Adjuntar las 2 primeras páginas de la escritura
            </label>
          </div>

          <div>
            <Label className="mb-2 block">Documentos del expediente a adjuntar ({numSeleccionados})</Label>
            {documentos.length === 0 ? (
              <p className="text-xs text-gray-500">No hay documentos en el expediente.</p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2" data-testid="eevid-docs-list">
                {documentos.map((d) => (
                  <label
                    key={d.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                    data-testid={`eevid-doc-${d.id}`}
                  >
                    <Checkbox checked={!!seleccionados[d.id]} onCheckedChange={() => toggleDoc(d.id)} />
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-xs flex-1 truncate">{d.nombre_original || d.nombre_archivo}</span>
                    <span className="text-[10px] text-gray-400">{d.tipo}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {numSeleccionados === 0 && !incluirEscritura && (
            <div className="flex items-center gap-2 text-amber-700 text-xs">
              <AlertCircle className="w-4 h-4" /> Selecciona al menos un adjunto antes de enviar.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="eevid-cancel-btn">Cancelar</Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleSend}
            disabled={sending || (numSeleccionados === 0 && !incluirEscritura)}
            data-testid="eevid-send-btn"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {sending ? 'Enviando…' : 'Enviar certificado'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
